import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

// ── Interfaces ──

export interface IEventoCategoria {
  id: number;
  nome: string;
  icone?: string;
  cor?: string;
  ordem: number;
}

export interface IChecklistTemplate {
  id: number;
  grupo_id: number;
  descricao: string;
  obrigatorio: boolean;
  responsavel_tipo: string;
  ordem: number;
}

export interface IChecklistTemplateGrupo {
  id: number;
  nome: string;
  descricao?: string;
  itens: IChecklistTemplate[];
}

export interface IEventoListItem {
  id: number;
  nome: string;
  artista?: string;
  dt_evento?: string;
  local?: string;
  cidade?: string;
  uf?: string;
  valor_orcamento: number;
  status: string;
  empresa_id: number;
  empresa_nome?: string;
  total_contratos: number;
  total_assinados: number;
  total_nfs_ok: number;
  checklist_total: number;
  checklist_concluido: number;
  total_alertas: number;
  ultimo_status_contrato?: string;
  kanban_projeto_id?: number;
  dt_criacao?: string;
  logo_url?: string;
}

export interface IEventoDetalhe extends IEventoListItem {
  descricao?: string;
  criado_por_nome?: string;
  valor_realizado: number;
  valor_pendente: number;
}

export interface IEventoCreate {
  nome: string;
  artista?: string;
  dt_evento?: string;
  local?: string;
  cidade?: string;
  uf?: string;
  valor_orcamento: number;
  status?: string;
  descricao?: string;
  empresa_id: number;
  checklist_template_grupo_id?: number;
}

export interface IEventoUpdate {
  nome?: string;
  artista?: string;
  dt_evento?: string;
  local?: string;
  cidade?: string;
  uf?: string;
  valor_orcamento?: number;
  status?: string;
  descricao?: string;
}

export interface IEventoContrato {
  id: number;
  evento_id: number;
  categoria_id?: number;
  categoria_nome?: string;
  categoria_cor?: string;
  categoria_icone?: string;
  fornecedor_nome: string;
  fornecedor_documento?: string;
  valor: number;
  status: string;
  nf_numero?: string;
  nf_valor?: number;
  nf_retencao_iss?: string;
  nf_retencao_ir?: string;
  nf_retencao_csll?: string;
  nf_retencao_cofins?: string;
  nf_retencao_pis?: string;
  nf_retencao_inss?: string;
  contrato_hash?: string;
  responsavel_id?: number;
  responsavel_nome?: string;
  dt_vencimento?: string;
  dt_assinatura?: string;
  assinado_por?: string;
  dt_pagamento?: string;
  observacao?: string;
  kanban_card_id?: number;
  kanban_coluna?: string;
  servicos?: IEventoServico[];
  dt_criacao?: string;
  arquivado?: boolean;
  /** Qual modelo do evento gera o contrato (`servico`, `produto`, `parceria_ab`...). */
  contrato_tipo?: string;
}

export interface IEventoServico {
  id: number;
  nome: string;
  categoria_id: number;
  categoria_nome?: string;
  categoria_cor?: string;
  ordem: number;
}

export interface IEventoContratoCreate {
  fornecedor_nome: string;
  fornecedor_documento?: string;
  valor: number;
  categoria_id?: number;
  responsavel_id?: number;
  dt_vencimento?: string;
  observacao?: string;
  servico_ids?: number[];
  /** Sem ele o contrato nasce de prestação de serviço. */
  contrato_tipo?: string;
  relatorio_emails?: string[];
  relatorio_dias?: number[];
  relatorio_hora?: string;
  relatorio_data?: string | null;
}

export interface IChecklistAnexo {
  id: number;
  checklist_item_id: number;
  nome: string;
  content_type?: string;
  tamanho?: number;
  url?: string;
  usuario_nome?: string;
  dt_criacao?: string;
}

export interface IEventoChecklistItem {
  id: number;
  evento_id: number;
  descricao: string;
  obrigatorio: boolean;
  responsavel_tipo: string;
  concluido: boolean;
  status: 'pendente' | 'concluido' | 'rejeitado';
  concluido_por_nome?: string;
  dt_conclusao?: string;
  ordem: number;
  anexos?: IChecklistAnexo[];
}

export interface IEventoDocumento {
  id: number;
  evento_id: number;
  contrato_id?: number;
  tipo: string;
  nome: string;
  content_type?: string;
  tamanho?: number;
  uploaded_por_nome?: string;
  dt_criacao?: string;
  origem?: 'evento' | 'kanban' | 'checklist';
  card_titulo?: string;
  url?: string;
}

export interface IEventoHistorico {
  id: number;
  evento_id: number;
  acao: string;
  descricao: string;
  usuario_nome?: string;
  dados_json?: any;
  dt_criacao?: string;
}

export interface IFinanceiroPorCategoria {
  categoria_nome: string;
  categoria_cor?: string;
  categoria_icone?: string;
  valor_total: number;
  valor_pago: number;
  total_contratos: number;
}

export interface IFinanceiroPorEvento {
  evento_id: number;
  evento_nome: string;
  valor_orcado: number;
  valor_pago: number;
  valor_pendente: number;
}

export interface IEventoFinanceiro {
  orcamento_total: number;
  realizado: number;
  pendente: number;
  por_categoria: IFinanceiroPorCategoria[];
  por_evento: IFinanceiroPorEvento[];
}

export interface IEventoAlerta {
  evento_id: number;
  evento_nome: string;
  contrato_id?: number;
  fornecedor_nome?: string;
  tipo: string;
  descricao: string;
}

// Painel executivo: contratos por origem (robô x pessoa) e as rodadas do robô no monitoramento.
export interface IPainelContagem {
  total: number;
  robo: number;
}

// Tempo da abertura do contrato até a etapa (gerar, enviar), por quem fez: mediana e p90 em segundos.
export interface ITempoEtapa {
  n: number;
  mediana_s: number | null;
  p90_s: number | null;
}

export interface IPainelContratos {
  total: number;
  robo: number;
  manual: number;
  valor_total: number;
  valor_robo: number;
  com_valor: number; // contratos com valor informado (a média do valor é só entre eles)
  sem_valor: number; // fora os cancelados
  assinados: number;
  assinados_robo: number;
  enviados: number; // chegaram ao envio para assinatura (do enviado ao pago)
  aguardando: number; // em "enviado para assinatura" agora
  parados: number; // desses, enviados há mais de 7 dias (pela data no histórico)
  dias_ate_assinatura: number | null;
  por_status: (IPainelContagem & { status: string })[];
  por_evento: (IPainelContagem & {
    evento_id: number;
    evento_nome: string;
    assinados: number;
    valor: number;
    aguardando: number;
    parados: number;
    por_status: Record<string, number>; // contratos do evento por etapa
  })[];
  por_tipo: (IPainelContagem & { tipo: string; rotulo: string })[];
  // por semana de abertura (segunda-feira): os abertos naquela semana, quantos deles já assinaram e o valor
  por_semana: { semana: string; robo: number; manual: number; assinados: number; valor: number }[];
  // Quem fez cada etapa no período, na data da etapa (1a vez de cada contrato)
  etapas: Record<
    'abertos' | 'gerados' | 'enviados' | 'assinados',
    { robo: number; pessoas: number }
  >;
  tempos: Record<'gerados' | 'enviados', Record<'robo' | 'pessoas', ITempoEtapa>>;
}

// As rodadas finalizadas de um dia, somadas pelo monitoramento.
export interface IDiaRobo {
  dia: string; // AAAA-MM-DD, em São Paulo
  rodadas: number;
  segundos: number; // soma das rodadas do dia
  inicio: string; // 1ª rodada, horário de São Paulo, sem fuso: lido literal
  fim: string; // fim da última rodada
}

export interface IPainelRobo {
  bot: string;
  desde?: string; // AAAA-MM-DD: o monitoramento entrega no máximo 62 dias
  limitado?: boolean; // o período escolhido passa dos 62 dias
  dias?: IDiaRobo[]; // do mais antigo ao mais recente, só dias com rodada
  em_andamento?: { exec_id: string; done: number; total: number | null }[];
}

// Pedido com `robo: false`: as rodadas do robô vêm de getPainelRobo.
export interface IPainelEventos {
  contratos: IPainelContratos;
}

// Números do evento na aba Gerencial (GET /eventos/{id}/resumo): os do painel executivo, só daquele evento.
export interface IEventoResumo {
  total: number;
  robo: number;
  assinados: number;
  aguardando: number; // enviados para assinatura, ainda sem assinar
  parados: number; // aguardando há mais de 7 dias
  valor_total: number; // sem os cancelados
  por_status: { status: string; total: number }[];
}

// ── Hook ──

export const useEventos = () => {
  // Eventos
  const getEventos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoListItem[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IEventoListItem[]>>('/api/v1/eventos/', {
      params: filters,
    });
    return res.data;
  };

  const getEvento = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoDetalhe>> => {
    const eventoId = key.queryKey?.[1];
    const res = await api.get<IDataNoPagination<IEventoDetalhe>>(`/api/v1/eventos/${eventoId}`);
    return res.data;
  };

  const createEvento = async (data: IEventoCreate): Promise<IDataNoPagination<IEventoDetalhe>> => {
    const res = await api.post<IDataNoPagination<IEventoDetalhe>>('/api/v1/eventos/', data);
    return res.data;
  };

  const updateEvento = async ({
    id,
    data,
  }: {
    id: number;
    data: IEventoUpdate;
  }): Promise<IDataNoPagination<IEventoDetalhe>> => {
    const res = await api.put<IDataNoPagination<IEventoDetalhe>>(`/api/v1/eventos/${id}`, data);
    return res.data;
  };

  const deleteEvento = async (id: number) => {
    const res = await api.delete(`/api/v1/eventos/${id}`);
    return res.data;
  };

  // Financeiro
  const getFinanceiro = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoFinanceiro>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IEventoFinanceiro>>('/api/v1/eventos/financeiro', {
      params: filters,
    });
    return res.data;
  };

  const getPainel = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IPainelEventos>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IPainelEventos>>('/api/v1/eventos/painel', {
      params: filters,
    });
    return res.data;
  };

  const getPainelRobo = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IPainelRobo>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IPainelRobo>>('/api/v1/eventos/painel/robo', {
      params: filters,
    });
    return res.data;
  };

  // Alertas
  const getAlertas = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoAlerta[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IEventoAlerta[]>>('/api/v1/eventos/alertas', {
      params: filters,
    });
    return res.data;
  };

  // Categorias
  const getCategorias = async (): Promise<IDataNoPagination<IEventoCategoria[]>> => {
    const res = await api.get<IDataNoPagination<IEventoCategoria[]>>('/api/v1/eventos/categorias');
    return res.data;
  };

  const createCategoria = async (data: Omit<IEventoCategoria, 'id'>) => {
    const res = await api.post('/api/v1/eventos/categorias', data);
    return res.data;
  };

  const updateCategoria = async ({ id, data }: { id: number; data: Partial<IEventoCategoria> }) => {
    const res = await api.put(`/api/v1/eventos/categorias/${id}`, data);
    return res.data;
  };

  const deleteCategoria = async (id: number) => {
    const res = await api.delete(`/api/v1/eventos/categorias/${id}`);
    return res.data;
  };

  // Serviços
  const getServicos = async (
    key?: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoServico[]>> => {
    const categoriaId = key?.queryKey?.[1];
    const params = categoriaId ? { categoria_id: categoriaId } : {};
    const res = await api.get<IDataNoPagination<IEventoServico[]>>('/api/v1/eventos/servicos', {
      params,
    });
    return res.data;
  };

  const createServico = async (data: { nome: string; categoria_id: number; ordem?: number }) => {
    const res = await api.post('/api/v1/eventos/servicos', data);
    return res.data;
  };

  const updateServico = async ({
    id,
    data,
  }: {
    id: number;
    data: { nome?: string; categoria_id?: number; ordem?: number };
  }) => {
    const res = await api.put(`/api/v1/eventos/servicos/${id}`, data);
    return res.data;
  };

  const deleteServico = async (id: number) => {
    const res = await api.delete(`/api/v1/eventos/servicos/${id}`);
    return res.data;
  };

  // Checklist Template — Grupos
  const getChecklistTemplateGrupos = async (): Promise<
    IDataNoPagination<IChecklistTemplateGrupo[]>
  > => {
    const res = await api.get<IDataNoPagination<IChecklistTemplateGrupo[]>>(
      '/api/v1/eventos/checklist-template-grupos',
    );
    return res.data;
  };

  const createChecklistTemplateGrupo = async (data: {
    nome: string;
    descricao?: string;
  }): Promise<IDataNoPagination<IChecklistTemplateGrupo>> => {
    const res = await api.post<IDataNoPagination<IChecklistTemplateGrupo>>(
      '/api/v1/eventos/checklist-template-grupos',
      data,
    );
    return res.data;
  };

  const updateChecklistTemplateGrupo = async ({
    id,
    data,
  }: {
    id: number;
    data: { nome?: string; descricao?: string };
  }): Promise<IDataNoPagination<IChecklistTemplateGrupo>> => {
    const res = await api.put<IDataNoPagination<IChecklistTemplateGrupo>>(
      `/api/v1/eventos/checklist-template-grupos/${id}`,
      data,
    );
    return res.data;
  };

  const deleteChecklistTemplateGrupo = async (id: number) => {
    const res = await api.delete(`/api/v1/eventos/checklist-template-grupos/${id}`);
    return res.data;
  };

  const createChecklistTemplateGrupoFromEvento = async ({
    eventoId,
    nome,
  }: {
    eventoId: number;
    nome: string;
  }): Promise<IDataNoPagination<IChecklistTemplateGrupo>> => {
    const res = await api.post<IDataNoPagination<IChecklistTemplateGrupo>>(
      `/api/v1/eventos/checklist-template-grupos/from-evento/${eventoId}`,
      null,
      { params: { nome } },
    );
    return res.data;
  };

  // Checklist Template — Itens
  const getChecklistTemplate = async (): Promise<IDataNoPagination<IChecklistTemplate[]>> => {
    const res = await api.get<IDataNoPagination<IChecklistTemplate[]>>(
      '/api/v1/eventos/checklist-template',
    );
    return res.data;
  };

  const createChecklistTemplate = async (
    data: Omit<IChecklistTemplate, 'id'>,
  ): Promise<IDataNoPagination<IChecklistTemplate>> => {
    const res = await api.post<IDataNoPagination<IChecklistTemplate>>(
      '/api/v1/eventos/checklist-template',
      data,
    );
    return res.data;
  };

  const updateChecklistTemplate = async ({
    id,
    data,
  }: {
    id: number;
    data: Partial<Omit<IChecklistTemplate, 'id'>>;
  }): Promise<IDataNoPagination<IChecklistTemplate>> => {
    const res = await api.put<IDataNoPagination<IChecklistTemplate>>(
      `/api/v1/eventos/checklist-template/${id}`,
      data,
    );
    return res.data;
  };

  const deleteChecklistTemplate = async (id: number) => {
    const res = await api.delete(`/api/v1/eventos/checklist-template/${id}`);
    return res.data;
  };

  // Checklist do evento — CRUD de itens individuais
  const createChecklistItem = async ({
    eventoId,
    data,
  }: {
    eventoId: number;
    data: { descricao: string; obrigatorio?: boolean; responsavel_tipo?: string; ordem?: number };
  }) => {
    const res = await api.post(`/api/v1/eventos/${eventoId}/checklist`, data);
    return res.data;
  };

  const updateChecklistItem = async ({
    eventoId,
    itemId,
    data,
  }: {
    eventoId: number;
    itemId: number;
    data: { descricao?: string; obrigatorio?: boolean; responsavel_tipo?: string; ordem?: number };
  }) => {
    const res = await api.put(`/api/v1/eventos/${eventoId}/checklist/${itemId}`, data);
    return res.data;
  };

  const deleteChecklistItem = async ({
    eventoId,
    itemId,
  }: {
    eventoId: number;
    itemId: number;
  }) => {
    const res = await api.delete(`/api/v1/eventos/${eventoId}/checklist/${itemId}`);
    return res.data;
  };

  // Contratos
  const getContratos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoContrato[]>> => {
    const eventoId = key.queryKey?.[1];
    const res = await api.get<IDataNoPagination<IEventoContrato[]>>(
      `/api/v1/eventos/${eventoId}/contratos`,
    );
    return res.data;
  };

  const getResumoEvento = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoResumo>> => {
    const eventoId = key.queryKey?.[1];
    const res = await api.get<IDataNoPagination<IEventoResumo>>(
      `/api/v1/eventos/${eventoId}/resumo`,
    );
    return res.data;
  };

  // Só os arquivados: o "Arquivar card" do quadro marca o contrato (tela Arquivados do evento).
  const getContratosArquivados = async (
    eventoId: number,
  ): Promise<IDataNoPagination<IEventoContrato[]>> => {
    const res = await api.get<IDataNoPagination<IEventoContrato[]>>(
      `/api/v1/eventos/${eventoId}/contratos`,
      { params: { arquivados: true } },
    );
    return res.data;
  };

  // Desarquivar o contrato devolve o card ao quadro, na coluna em que estava.
  const desarquivarContrato = async ({
    eventoId,
    contratoId,
  }: {
    eventoId: number;
    contratoId: number;
  }) => {
    const res = await api.put(`/api/v1/eventos/${eventoId}/contratos/${contratoId}`, {
      arquivado: false,
    });
    return res.data;
  };

  const createContrato = async ({
    eventoId,
    data,
  }: {
    eventoId: number;
    data: IEventoContratoCreate;
  }) => {
    const res = await api.post(`/api/v1/eventos/${eventoId}/contratos`, data);
    return res.data;
  };

  const updateContratoStatus = async ({
    eventoId,
    contratoId,
    data,
  }: {
    eventoId: number;
    contratoId: number;
    data: { status: string; nf_numero?: string; assinado_por?: string; dt_pagamento?: string };
  }) => {
    const res = await api.patch(`/api/v1/eventos/${eventoId}/contratos/${contratoId}/status`, data);
    return res.data;
  };

  const deleteContrato = async ({
    eventoId,
    contratoId,
  }: {
    eventoId: number;
    contratoId: number;
  }) => {
    const res = await api.delete(`/api/v1/eventos/${eventoId}/contratos/${contratoId}`);
    return res.data;
  };

  // Checklist
  const getChecklist = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoChecklistItem[]>> => {
    const eventoId = key.queryKey?.[1];
    const res = await api.get<IDataNoPagination<IEventoChecklistItem[]>>(
      `/api/v1/eventos/${eventoId}/checklist`,
    );
    return res.data;
  };

  const toggleChecklistItem = async ({
    eventoId,
    itemId,
  }: {
    eventoId: number;
    itemId: number;
  }) => {
    const res = await api.patch(`/api/v1/eventos/${eventoId}/checklist/${itemId}/toggle`);
    return res.data;
  };

  const setChecklistStatus = async ({
    eventoId,
    itemId,
    status,
  }: {
    eventoId: number;
    itemId: number;
    status: string;
  }) => {
    const res = await api.patch(`/api/v1/eventos/${eventoId}/checklist/${itemId}/status`, {
      status,
    });
    return res.data;
  };

  // Checklist Anexos
  const uploadChecklistAnexo = async ({
    eventoId,
    itemId,
    file,
  }: {
    eventoId: number;
    itemId: number;
    file: File;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/api/v1/eventos/${eventoId}/checklist/${itemId}/anexos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  const deleteChecklistAnexo = async (anexoId: number) => {
    const res = await api.delete(`/api/v1/eventos/checklist-anexos/${anexoId}`);
    return res.data;
  };

  // Documentos
  const getDocumentos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoDocumento[]>> => {
    const eventoId = key.queryKey?.[1];
    const res = await api.get<IDataNoPagination<IEventoDocumento[]>>(
      `/api/v1/eventos/${eventoId}/documentos`,
    );
    return res.data;
  };

  const uploadDocumento = async ({
    eventoId,
    file,
    tipo,
    contratoId,
    contratoTipo,
  }: {
    eventoId: number;
    file: File;
    tipo: string;
    contratoId?: number;
    /** Só no template de contrato: o tipo de contrato que ele gera. */
    contratoTipo?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', tipo);
    if (contratoId) formData.append('contrato_id', String(contratoId));
    if (contratoTipo) formData.append('contrato_tipo', contratoTipo);
    const res = await api.post(`/api/v1/eventos/${eventoId}/documentos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  const downloadDocumento = async ({
    eventoId,
    docId,
  }: {
    eventoId: number;
    docId: number;
  }): Promise<IDataNoPagination<{ url: string; filename: string }>> => {
    const res = await api.get(`/api/v1/eventos/${eventoId}/documentos/${docId}/download`);
    return res.data;
  };

  // Logo
  const uploadLogo = async ({ eventoId, file }: { eventoId: number; file: File }) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/api/v1/eventos/${eventoId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  const deleteLogo = async (eventoId: number) => {
    const res = await api.delete(`/api/v1/eventos/${eventoId}/logo`);
    return res.data;
  };

  // Histórico
  const getHistorico = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEventoHistorico[]>> => {
    const eventoId = key.queryKey?.[1];
    const res = await api.get<IDataNoPagination<IEventoHistorico[]>>(
      `/api/v1/eventos/${eventoId}/historico`,
    );
    return res.data;
  };

  return {
    getEventos,
    getEvento,
    createEvento,
    updateEvento,
    deleteEvento,
    getFinanceiro,
    getPainel,
    getPainelRobo,
    getAlertas,
    getCategorias,
    createCategoria,
    updateCategoria,
    deleteCategoria,
    getServicos,
    createServico,
    updateServico,
    deleteServico,
    getChecklistTemplateGrupos,
    createChecklistTemplateGrupo,
    createChecklistTemplateGrupoFromEvento,
    updateChecklistTemplateGrupo,
    deleteChecklistTemplateGrupo,
    getChecklistTemplate,
    createChecklistTemplate,
    updateChecklistTemplate,
    deleteChecklistTemplate,
    createChecklistItem,
    updateChecklistItem,
    deleteChecklistItem,
    getContratos,
    getResumoEvento,
    getContratosArquivados,
    desarquivarContrato,
    createContrato,
    updateContratoStatus,
    deleteContrato,
    getChecklist,
    toggleChecklistItem,
    setChecklistStatus,
    uploadChecklistAnexo,
    deleteChecklistAnexo,
    getDocumentos,
    uploadDocumento,
    downloadDocumento,
    uploadLogo,
    deleteLogo,
    getHistorico,
  };
};
